import { useMemo, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { miningPoolsState, selectedMiningAccountState } from '../../api';
import { loadBalancerAPI } from '../../api/load-balancer';

export type TSummaryMachineInfo = {
  totalHashrate: number;
  totalOffline: number;
  totalOnline: number;
};

export const useMachineInfo = () => {
  // States
  const miningAccountDetail = useRecoilValue(selectedMiningAccountState);
  const miningPoolValues = useRecoilValue(miningPoolsState);

  // Miner infos
  const resultMinerList = loadBalancerAPI.hookWrapperQuery.useGetMinerDetails(
    miningPoolValues,
    miningAccountDetail,
    miningAccountDetail && !!miningAccountDetail.id
  );

  const loadingGetMiner = [resultMinerList].some((resultMinerListItem) => resultMinerListItem.isLoading);
  const refetchingGetMiner = [resultMinerList].some((resultMinerListItem) => resultMinerListItem.isRefetching);

  const formatResultMinerList = useMemo(() => {
    const statMinerFiltered = [resultMinerList].filter((statMinerFilterItem) => statMinerFilterItem.data);
    if (!statMinerFiltered || !statMinerFiltered.length) return [];
    const statMinerDetailFormatted = statMinerFiltered.map((statMinerItem) => statMinerItem.data);
    return statMinerDetailFormatted.flat(1);
  }, [resultMinerList]);

  const summaryMachineInfo: TSummaryMachineInfo = useMemo(() => {
    let totalHashrate = 0;
    let totalOffline = 0;
    let totalOnline = 0;
    if (!formatResultMinerList || !formatResultMinerList.length) {
      return {
        totalHashrate,
        totalOffline,
        totalOnline,
      };
    }

    formatResultMinerList.forEach((item) => {
      totalHashrate += item.totalHashRate;
      if (item.status === 'Offline') {
        totalOffline += +item.cardCounts;
      } else if (item.status === 'Online') {
        totalOnline += +item.cardCounts;
      }
    });
    return {
      totalHashrate,
      totalOffline,
      totalOnline,
    };
  }, [formatResultMinerList]);

  // Stat miner infos
  const resultStatList = loadBalancerAPI.hookWrapperQuery.useGetStatDetails(
    miningPoolValues,
    miningAccountDetail,
    miningAccountDetail && !!miningAccountDetail.id
  );

  const loadingGetStatMiner = [resultStatList].some((resultStatListItem) => resultStatListItem.isLoading);
  const refetchingGetStatMiner = [resultStatList].some((resultStatListItem) => resultStatListItem.isRefetching);

  const formatResultStatList = useMemo(() => {
    const statListFiltered = [resultStatList].filter((statMinerFilterItem) => statMinerFilterItem.data);
    if (!statListFiltered || !statListFiltered.length) return [];
    const flatStatListFiltered = statListFiltered.map((statMinerItem) => statMinerItem.data).flat();
    const mergedData = flatStatListFiltered.reduce((acc, item) => {
      const existingItem = acc.find((entry) => entry.cardType === item.cardType);
      if (existingItem) {
        existingItem.cardCount += item.cardCount;
        existingItem.hashrate += item.hashrate;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, []);
    return mergedData;
  }, [resultStatList]);

  const refetchGetStatMiner = useCallback(() => {
    [resultStatList].forEach((resultStatListItem) => {
      if (resultStatListItem.isSuccess) {
        resultStatListItem.refetch();
      }
    });
  }, [resultStatList]);

  const refetchGetMinerList = useCallback(() => {
    [resultMinerList].forEach((resultMinerListItem) => {
      if (resultMinerListItem.isSuccess) {
        resultMinerListItem.refetch();
      }
    });
  }, [resultMinerList]);

  return {
    minerListData: formatResultMinerList,
    loadingGetStatMiner,
    statMinerList: formatResultStatList,
    summaryMachineInfo,
    loadingGetMiner,
    refetchingGetMiner,
    refetchingGetStatMiner,
    refetchGetStatMiner,
    refetchGetMinerList,
  };
};
